"use client"

import { motion, useReducedMotion, type HTMLMotionProps } from "motion/react"
import { forwardRef, type HTMLAttributes, type ReactNode } from "react"
import { cn } from "@strivelabs/ui-core"
import {
  DURATION_BASE,
  DURATION_QUICK,
  EASE_OUT,
  IconTile,
  type IconTileProps,
} from "@strivelabs/ui-core/v2"

/**
 * Centred placeholder for a surface with nothing to show yet.
 *
 * Composed from parts rather than driven by props, so a caller can drop the
 * description, put two buttons in the actions row, or slot an illustration where
 * the icon goes without the component growing a prop for each case.
 *
 * The root animates as one block rather than cascading its parts in. The parts
 * are separate components, so a stagger would have to travel through motion's
 * variant propagation to reach them — and an empty state is a single message,
 * not four things that each deserve their own arrival. One entrance also keeps
 * every part a plain element, free to be used outside this root.
 */
export const EmptyState = forwardRef<HTMLDivElement, HTMLMotionProps<"div">>(
  function EmptyState({ className, ...props }, ref) {
    const reduce = useReducedMotion()

    return (
      <motion.div
        ref={ref}
        data-slot="empty-state"
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
        animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
        transition={{
          duration: reduce ? DURATION_QUICK : DURATION_BASE,
          ease: EASE_OUT,
        }}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-2 px-6 py-12 text-center",
          className,
        )}
        {...props}
      />
    )
  },
)

EmptyState.displayName = "EmptyState"

export interface EmptyStateIconProps extends Omit<IconTileProps, "children"> {
  children?: ReactNode
}

/**
 * Frames the glyph on the shared {@link IconTile} surface.
 *
 * Defaults to the `frame` variant at `lg` — the quiet double-container look — so
 * an empty state does not invent a third kind of icon well. Both are overridable
 * for a state that wants a tinted or solid tile. Hidden from assistive
 * technology: the title beside it already says what the state is, and a decorative
 * glyph announced on its own says nothing.
 */
export const EmptyStateIcon = forwardRef<HTMLSpanElement, EmptyStateIconProps>(
  function EmptyStateIcon({ className, variant = "frame", size = "lg", ...props }, ref) {
    return (
      <IconTile
        ref={ref}
        aria-hidden="true"
        data-slot="empty-state-icon"
        variant={variant}
        size={size}
        className={cn("mb-1", className)}
        {...props}
      />
    )
  },
)

EmptyStateIcon.displayName = "EmptyStateIcon"

/** Heading depth for {@link EmptyStateTitle}. */
export type EmptyStateTitleLevel = 2 | 3 | 4 | 5

export interface EmptyStateTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /**
   * Heading depth. Defaults to 3.
   *
   * An empty state can fill a page region or sit inside a card that already has
   * a heading, and a fixed depth would skip a level in one of the two. Set it to
   * whatever the surrounding outline needs.
   */
  level?: EmptyStateTitleLevel
}

export const EmptyStateTitle = forwardRef<HTMLHeadingElement, EmptyStateTitleProps>(
  function EmptyStateTitle({ className, level = 3, ...props }, ref) {
    const Heading = `h${level}` as const

    return (
      <Heading
        ref={ref}
        data-slot="empty-state-title"
        className={cn(
          "text-balance break-words text-v2-base font-semibold text-v2-foreground",
          className,
        )}
        {...props}
      />
    )
  },
)

EmptyStateTitle.displayName = "EmptyStateTitle"

export const EmptyStateDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function EmptyStateDescription({ className, ...props }, ref) {
  return (
    <p
      ref={ref}
      data-slot="empty-state-description"
      className={cn(
        "max-w-sm text-pretty break-words text-v2-sm text-v2-muted-foreground",
        className,
      )}
      {...props}
    />
  )
})

EmptyStateDescription.displayName = "EmptyStateDescription"

export const EmptyStateActions = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function EmptyStateActions({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        data-slot="empty-state-actions"
        className={cn("flex flex-wrap items-center justify-center gap-2 pt-2", className)}
        {...props}
      />
    )
  },
)

EmptyStateActions.displayName = "EmptyStateActions"
